import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import {
  getCvssBgColor,
  getCvssColor,
  getCvssLabel,
} from '@/sections/poe/processors/wiz/parsers';
import type {
  WizParsedData,
  WizRenderContext,
} from '@/sections/poe/processors/wiz/types';
import { cn } from '@/utils/classname';

export function renderSimpleVuln(
  parsed: WizParsedData,
  ctx: WizRenderContext
): void {
  const { wizData, wizId, isSimpleFormat } = parsed;
  const { sections, tempTabs, mainTabKeys } = ctx;

  if (isSimpleFormat && mainTabKeys.includes('DETECTION')) {
    sections['wiz_simple_vuln'] = (
      <div className="space-y-4">
        <div className="border-error border-l-4 p-4">
          <div className="flex items-center">
            <ShieldExclamationIcon className="text-error mr-2 size-5" />
            <h4 className="text-chariot-text-primary font-semibold">
              Vulnerability Detection
            </h4>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Vulnerability Details */}
          <div className="border-chariot-stroke rounded-lg border p-4">
            <h5 className="text-chariot-text-primary mb-3 font-medium">
              Vulnerability Details
            </h5>
            <div className="space-y-3">
              {wizData.cvss_base != null && (
                <div className="flex items-start justify-between">
                  <span className="text-chariot-text-secondary font-medium">
                    CVSS Score:
                  </span>
                  <div className="text-right">
                    <span
                      className={cn(
                        'text-lg font-bold',
                        getCvssColor(wizData.cvss_base)
                      )}
                    >
                      {wizData.cvss_base}
                    </span>
                    <span
                      className={cn(
                        'ml-2 rounded px-2 py-1 text-xs',
                        getCvssBgColor(wizData.cvss_base),
                        getCvssColor(wizData.cvss_base)
                      )}
                    >
                      {getCvssLabel(wizData.cvss_base)}
                    </span>
                  </div>
                </div>
              )}
              {wizData.os && (
                <div className="flex items-start justify-between">
                  <span className="text-chariot-text-secondary font-medium">
                    Operating System:
                  </span>
                  <span className="text-chariot-text-primary font-mono">
                    {wizData.os}
                  </span>
                </div>
              )}
              {wizId && (
                <div className="flex items-start justify-between">
                  <span className="text-chariot-text-secondary font-medium">
                    Wiz ID:
                  </span>
                  <code className="bg-chariot-background-secondary rounded px-2 py-1 text-xs">
                    {wizId}
                  </code>
                </div>
              )}
            </div>
          </div>

          {/* Version Information */}
          <div className="border-chariot-stroke rounded-lg border p-4">
            <h5 className="text-chariot-text-primary mb-3 font-medium">
              Version Information
            </h5>
            <div className="space-y-3">
              {wizData.version && (
                <div>
                  <span className="text-chariot-text-secondary mb-1 block font-medium">
                    Current Version:
                  </span>
                  <code className="bg-error-selected text-error block rounded px-3 py-2 text-sm">
                    {wizData.version}
                  </code>
                </div>
              )}
              {wizData.fixed_version && (
                <div>
                  <span className="text-chariot-text-secondary mb-1 block font-medium">
                    Fixed Version:
                  </span>
                  <code className="bg-success-selected text-success block rounded px-3 py-2 text-sm">
                    {wizData.fixed_version}
                  </code>
                </div>
              )}
              {wizData.version && wizData.fixed_version && (
                <div className="border-chariot-stroke mt-3 border-t pt-3">
                  <div className="text-chariot-text-secondary flex items-center text-sm">
                    <InformationCircleIcon className="mr-2 size-4" />
                    <span>Update required to resolve vulnerability</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Required */}
        {wizData.version && wizData.fixed_version && (
          <div className="border-warning border-l-4 p-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="text-warning mt-0.5 mr-3 size-5 shrink-0" />
              <div>
                <h5 className="text-chariot-text-primary mb-2 font-semibold">
                  Action Required
                </h5>
                <p className="text-chariot-text-primary text-sm">
                  This system is running <strong>{wizData.version}</strong>{' '}
                  which contains known vulnerabilities. Update to{' '}
                  <strong>{wizData.fixed_version}</strong> or later to resolve
                  this security issue.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
    tempTabs['DETECTION'].push({
      label: 'Vulnerability',
      value: 'wiz_simple_vuln',
    });
  }
}
